import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Loader2, Trash2, X } from 'lucide-react';
import { http } from '../api/http';
import { initials } from '../lib/dates';
import { EMPTY_PHOTOS, photoDirectory, type PhotoDirectory } from '../lib/photos';
import { squarePhoto } from '../lib/squarePhoto';
import { useAuthStore } from '../store/auth';

// Photos de profil dans l'application du moniteur. L'annuaire de l'école (`/v1/photos`) donne
// l'adresse versionnée de chaque photo ; la route est protégée (jeton en en-tête), l'image est donc
// lue ici puis affichée depuis une adresse locale, gardée tant que sa version ne change pas.

export const PHOTO_DIRECTORY_KEY = 'photo-directory';
const PHOTO_KEY = 'profile-photo';

export function usePhotoDirectory(): PhotoDirectory {
  const userId = useAuthStore((s) => s.user?.id);
  const { data } = useQuery({
    queryKey: [PHOTO_DIRECTORY_KEY, userId],
    queryFn: async () => (await http.get<unknown>('/v1/photos')).data,
    select: photoDirectory,
    enabled: !!userId,
    staleTime: 5 * 60_000,
    retry: false,
  });
  return data ?? EMPTY_PHOTOS;
}

async function loadPhoto(url: string): Promise<string> {
  const { data } = await http.get<Blob>(url, { responseType: 'blob' });
  const local = URL.createObjectURL(data);
  try {
    const probe = new Image();
    probe.src = local;
    await probe.decode();
    return local;
  } catch (error) {
    URL.revokeObjectURL(local);
    throw error;
  }
}

/** Image prête à afficher d'une photo, ou `undefined` (initiales). */
export function usePhoto(url?: string | null): string | undefined {
  const client = useQueryClient();
  useEffect(
    () =>
      client.getQueryCache().subscribe((event) => {
        const local = event.query.state.data;
        if (event.type === 'removed' && event.query.queryKey[0] === PHOTO_KEY && typeof local === 'string')
          URL.revokeObjectURL(local);
      }),
    [client],
  );
  return useQuery({
    queryKey: [PHOTO_KEY, url ?? ''],
    queryFn: () => loadPhoto(url as string),
    enabled: !!url,
    staleTime: Infinity,
    retry: false,
  }).data;
}

export function useStudentPhoto(candidateId?: string | null) {
  const directory = usePhotoDirectory();
  return usePhoto(candidateId ? directory.candidates[candidateId] : null);
}

export function useStaffPhoto(userId?: string | null) {
  const directory = usePhotoDirectory();
  return usePhoto(userId ? directory.staff[userId] : null);
}

/** Photo seule d'un élève, posée sur ses initiales ; rien sans photo. */
export function StudentPhoto({ candidateId, className = 'photo-cover' }: { candidateId?: string | null; className?: string }) {
  const src = useStudentPhoto(candidateId);
  return src ? <img src={src} alt="" className={className} /> : null;
}

/** Photo seule d'un compte de l'équipe, posée sur ses initiales ; rien sans photo. */
export function StaffPhoto({ userId, className = 'photo-cover' }: { userId?: string | null; className?: string }) {
  const src = useStaffPhoto(userId);
  return src ? <img src={src} alt="" className={className} /> : null;
}

const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const PHOTO_MAX_BYTES = 8 * 1024 * 1024;

const failure = (error: unknown, fallback: string) => {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof message === 'string' && message ? message : fallback;
};

/**
 * « Mon profil » : la photo du moniteur, celle de sa fiche d'équipe. Il l'ajoute, la change ou la
 * retire ; elle remplace ses initiales dans le planning du bureau, la messagerie et l'espace des
 * élèves.
 */
export function ProfileDialog({ onClose }: { onClose: () => void }) {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const url = usePhotoDirectory().staff[user?.id ?? ''];
  const photo = usePhoto(url);
  const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'Moniteur';
  const refresh = () => queryClient.invalidateQueries({ queryKey: [PHOTO_DIRECTORY_KEY] });
  const send = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', await squarePhoto(file), 'photo.jpg');
      return http.post('/v1/team/me/photo', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    onSuccess: refresh,
    onError: (e) => setError(failure(e, 'Envoi de la photo impossible.')),
  });
  const remove = useMutation({
    mutationFn: () => http.delete('/v1/team/me/photo'),
    onSuccess: refresh,
    onError: (e) => setError(failure(e, 'Retrait de la photo impossible.')),
  });
  const busy = send.isPending || remove.isPending;
  const choose = (file?: File) => {
    if (!file || busy) return;
    setError(null);
    if (!PHOTO_TYPES.includes(file.type)) return setError('Format non pris en charge : JPEG, PNG ou WebP attendu.');
    if (file.size > PHOTO_MAX_BYTES) return setError('Photo trop volumineuse (8 Mo au plus).');
    send.mutate(file);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Mon profil"
        className="w-full max-w-sm rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Mon profil</h2>
          <button onClick={onClose} aria-label="Fermer" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>
        <div className="flex flex-col items-center gap-4 px-5 py-6 text-center">
          <span className="relative grid h-24 w-24 place-items-center overflow-hidden rounded-full bg-gradient-to-br from-violet-500 to-violet-600 text-2xl font-semibold text-white">
            {initials(user?.firstName ?? 'M', user?.lastName ?? 'O')}
            {photo && <img src={photo} alt="" className="photo-cover" />}
            {busy && (
              <span className="absolute inset-0 grid place-items-center bg-white/60">
                <Loader2 size={22} className="animate-spin text-indigo-600" />
              </span>
            )}
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-900">{name}</p>
            <p className="text-xs text-slate-500">{user?.email}</p>
          </div>
          <input
            ref={input}
            type="file"
            hidden
            accept={PHOTO_TYPES.join(',')}
            aria-label="Fichier de la photo"
            disabled={busy}
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              e.currentTarget.value = '';
              choose(file);
            }}
          />
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={() => input.current?.click()}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              <Camera size={15} />
              {url ? 'Changer la photo' : 'Ajouter une photo'}
            </button>
            {url && (
              <button
                onClick={() => {
                  setError(null);
                  remove.mutate();
                }}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
              >
                <Trash2 size={15} />
                Retirer
              </button>
            )}
          </div>
          <p className="text-xs text-slate-500">
            Votre photo remplace vos initiales dans le planning, la messagerie et l’espace de vos élèves.
          </p>
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
